import React, { useEffect } from 'react';

import { makeStyles, Theme } from '@material-ui/core/styles';
import { hashHistory } from 'react-router';

import { BackLink, Typography } from '../../components/atoms';
import {
  OrderCollectionLarge,
  OrderDeliveryLarge,
} from '../../components/icons';
import { LoadingSpinner, Page } from '../../components/layouts';
import { NavList } from '../../components/molecules';
import { CampaignConsentDialog } from '../../components/partials';
import { PageHeader } from '../../components/partials/PageHeader';
import { analyticsService } from '../../core/services';
import { domUtils, urlUtils, utils } from '../../utils';
import { Track } from '../../weborder/analytics';
import * as Api from '../../weborder/Api';
import { DispatchProps, StateProps } from '../../weborder/component/Where';

type Props = StateProps & DispatchProps;

const useStyles = makeStyles((theme: Theme) => ({
  landing: {},

  landingNavList: {
    boxSizing: 'border-box',

    '& .landingTitle': {
      marginLeft: theme.spacing(2),
    },

    '& .orderTypeIcon .fd-icon': {
      fontSize: theme.typography.pxToRem(24),
    },
  },

  [theme.breakpoints.up('sm')]: {
    landingNavList: {
      padding: theme.spacing(0, 3),

      '& .landingTitle': {
        marginLeft: theme.spacing(4),
      },

      '& .orderTypeIcon .fd-icon': {
        fontSize: theme.typography.pxToRem(48),
      },

      '& .fd-list__item': {
        border: `1px solid ${theme.palette.grey[400]}`,
        marginBottom: theme.spacing(2),
        padding: theme.spacing(0, 3),

        '& .MuiListItemSecondaryAction-root': {
          right: theme.spacing(2),
        },

        '&:hover': {
          borderColor: theme.palette.primary.main,

          '& + li': {
            borderTop: `1px solid ${theme.palette.grey[400]}`,
          },
        },
      },
    },
  },

  [theme.breakpoints.up('md')]: {
    landingNavList: {
      padding: 0,
    },
  },
}));

export function Landing(props: Props): JSX.Element {
  const classes = useStyles();
  const {
    AppConfig: appConfig,
    CollectionSummaries: collectionSummaries,
    Customer: customer,
    LastRestaurantUrl: lastRestaurantUrl,
    translate,
  } = props;

  useEffect(() => {
    domUtils.skipToContent();
  }, []);

  useEffect(() => {
    const trackStartOrder = (): void => {
      const isElixir = utils.check.isElixirHosted();

      const eventData = {
        amp_isElixir: isElixir,
        amp_directMethodselection: true,
        amp_label: 'Start Order',
      };
      analyticsService.track(eventData);
    };

    trackStartOrder();
  }, []);

  useEffect(() => {
    if (appConfig.status === 'success') {
      redirectOnOrderOptionsStatus(appConfig.data);
    }
  }, [appConfig.status]);

  const redirectOnOrderOptionsStatus = (configData: Api.AppConfig): void => {
    const isDeliveryAndCollectionUnavailable =
      !configData.DeliveryEnabled && !configData.PickupEnabled;
    if (isDeliveryAndCollectionUnavailable) {
      hashHistory.replace('/unavailable');
    }

    const isDeliveryOnly =
      configData.DeliveryEnabled && !configData.PickupEnabled;
    if (isDeliveryOnly) {
      hashHistory.replace('/delivery');
    }

    const isCollectionOnly =
      !configData.DeliveryEnabled && configData.PickupEnabled;
    if (isCollectionOnly) {
      hashHistory.replace(
        urlUtils.getCollectionLink(configData, collectionSummaries),
      );
    }
  };

  if (appConfig.status !== 'success') {
    return <LoadingSpinner />;
  }

  const trackBackSelected = (): void => {
    const eventData = {
      amp_label: 'Back Selected',
      amp_view: 'Landing',
    };
    analyticsService.track(eventData);
  };

  const trackOrderTypeSelection = (channel: string): void => {
    const eventData = {
      amp_label: 'Order Method Selected',
      amp_methodSelected: channel,
      label: `${channel} selected`,
      section: 'Maps',
    };

    Track('Maps', `${channel} selected`, eventData);
  };

  const getCollectionLabel = (labelKey) => {
    const labels = {
      title: {
        dineIn: translate('Dine_in'),
        pickUp: `${translate('Collect')}/${translate('Dine_in')}`,
        default: translate('Collection'),
      },
    };

    if (appConfig.data.DineInEnabled && !appConfig.data.PickupEnabled) {
      return labels[labelKey].dineIn;
    }

    if (appConfig.data.DineInEnabled && appConfig.data.PickupEnabled) {
      return labels[labelKey].pickUp;
    }

    return labels[labelKey].default;
  };

  const orderOptions = [
    {
      elements: [
        <OrderCollectionLarge key="icon-2" className="orderTypeIcon" />,
        <Typography
          className="landingTitle"
          data-fd="landing-collection-button"
          key="title-2"
          variant="h2"
        >
          {getCollectionLabel('title')}
        </Typography>,
      ],
      linkCallback: () => trackOrderTypeSelection('Collection'),
      linkId: 'order-collection',
      linkPath: urlUtils.getCollectionLink(
        appConfig?.data,
        collectionSummaries,
      ),
    },
    {
      elements: [
        <OrderDeliveryLarge key="icon-1" className="orderTypeIcon" />,
        <Typography
          className="landingTitle"
          data-fd="landing-delivery-button"
          key="title-1"
          variant="h2"
        >
          {translate('Delivery')}
        </Typography>,
      ],
      linkCallback: () => trackOrderTypeSelection('Delivery'),
      linkId: 'order-delivery',
      linkPath: '/delivery',
    },
  ];

  const backLinkPath = lastRestaurantUrl === '/' ? null : lastRestaurantUrl;

  return (
    <div className={classes.landing}>
      <Page pageName="landing">
        <PageHeader
          showProfileMenu
          tabIndex={0}
          title={translate('How_do_you_want_to_order')}
        >
          {backLinkPath && (
            <BackLink
              label={translate('Back')}
              linkPath={backLinkPath}
              onClick={trackBackSelected}
            />
          )}
        </PageHeader>
        <article className="fd-content__main">
          <NavList items={orderOptions} className={classes.landingNavList} />
        </article>
      </Page>

      <CampaignConsentDialog customer={customer} />
    </div>
  );
}
